// Email a reviewed document as a PDF. Opened from the review screen's Email
// button; the send itself is the /export route with format "email", so the
// letter is filed in Patient Files exactly as a download would be.
//
// The To field is the same People typeahead the Session email uses: typing a
// name offers the practice's People records that have an email, picking one
// puts the primary address in To and any extra addresses in CC. Free text
// still works, separated by ";".
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import { personDisplayName } from "./RecipientSearch";

const BUTTON_WIDTH = 150;

// All non-empty email addresses on a person record, primary first.
export const personEmails = (p) =>
  ["email", "email2", "email3", "email4", "email5"]
    .map((f) => String(p?.[f] ?? "").trim())
    .filter(Boolean);

const splitList = (s) => String(s || "").split(/[;,]/).map((x) => x.trim()).filter(Boolean);
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export default function SendDocumentModal({
  show, onHide, title, people, initialTo, initialCc, initialSubject, initialMessage, onSend,
}) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setTo(initialTo || "");
    setCc(initialCc || "");
    setSubject(initialSubject || "");
    setMessage(initialMessage || "");
    setError("");
    setSending(false);
    setSuggestOpen(false);
  }, [show, initialTo, initialCc, initialSubject, initialMessage]);

  // The segment being typed: everything after the last ";", so recipients
  // already committed are left alone.
  const query = (() => {
    const idx = to.lastIndexOf(";");
    return (idx >= 0 ? to.slice(idx + 1) : to).trim().toLowerCase();
  })();

  const suggestions = suggestOpen && query.length >= 1
    ? (people || [])
      .filter((p) => {
        const emails = personEmails(p);
        if (!emails.length) return false;
        const hay = [personDisplayName(p), emails[0]].join(" ").toLowerCase();
        return hay.includes(query);
      })
      .slice(0, 8)
    : [];

  const pickPerson = (p) => {
    const [primary, ...extras] = personEmails(p);
    if (!primary) return;
    const idx = to.lastIndexOf(";");
    const committed = splitList(idx >= 0 ? to.slice(0, idx) : "");
    if (!committed.some((a) => a.toLowerCase() === primary.toLowerCase())) committed.push(primary);
    setTo(committed.join("; "));
    if (extras.length) {
      const ccList = splitList(cc);
      extras.forEach((e) => {
        const taken = [...committed, ...ccList].some((a) => a.toLowerCase() === e.toLowerCase());
        if (!taken) ccList.push(e);
      });
      setCc(ccList.join("; "));
    }
    setSuggestOpen(false);
  };

  const send = async () => {
    const toList = splitList(to);
    const ccList = splitList(cc);
    if (!toList.length) { setError("Enter at least one recipient email address."); return; }
    const bad = [...toList, ...ccList].filter((a) => !isEmail(a));
    if (bad.length) { setError(`These addresses look invalid: ${bad.join(", ")}`); return; }
    setError("");
    setSending(true);
    try {
      await onSend({ to: toList, cc: ccList, subject: subject.trim(), message: message.trim() });
      onHide();
    } catch (e) {
      // Stay open: the addresses and wording are still here to fix.
      setError(e?.message || "The email could not be sent.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show={show} onHide={sending ? undefined : onHide} centered size="lg" scrollable>
      <Modal.Header
        closeButton={!sending}
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        <Row className="g-2 mb-2">
          <Col xs={12} md={6}>
            <Form.Group className="position-relative">
              <Form.Label>To</Form.Label>
              <Form.Control
                type="text"
                autoComplete="off"
                placeholder="Type a name, or enter email addresses separated by ;"
                value={to}
                onChange={(e) => { setTo(e.target.value); setSuggestOpen(true); }}
                onFocus={() => setSuggestOpen(true)}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                disabled={sending}
              />
              {suggestions.length > 0 && (
                <div
                  className="list-group position-absolute w-100 shadow-sm"
                  style={{ zIndex: 1056, maxHeight: 240, overflowY: "auto" }}
                >
                  {suggestions.map((p) => {
                    const emails = personEmails(p);
                    return (
                      <button
                        key={p?._id?.$oid || p?._id || `${p?.firstName}-${p?.lastName}-${emails[0]}`}
                        type="button"
                        className="list-group-item list-group-item-action py-2"
                        onMouseDown={(e) => { e.preventDefault(); pickPerson(p); }}
                      >
                        <div className="fw-semibold text-truncate">{personDisplayName(p) || emails[0]}</div>
                        <div className="small text-muted text-truncate">
                          {emails[0]}
                          {emails.length > 1 && <span className="ms-1">(+{emails.length - 1} to CC)</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>CC</Form.Label>
              <Form.Control
                type="text"
                autoComplete="off"
                placeholder="Separate multiple addresses with ;"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                disabled={sending}
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-2">
          <Form.Label>Subject</Form.Label>
          <Form.Control type="text" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sending} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Message</Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending}
          />
        </Form.Group>
        <div className="small text-muted">
          The document is attached as a PDF, and a copy is filed in Patient Files when it is sent.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={send} disabled={sending || !to.trim()} style={{ width: BUTTON_WIDTH }}>
          {sending ? <Spinner size="sm" animation="border" /> : (<><i className="fa-solid fa-envelope me-2" />Send</>)}
        </Button>
        <Button variant="outline-secondary" onClick={onHide} disabled={sending} style={{ width: BUTTON_WIDTH }}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
